import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { connect } from 'react-redux';

// Import util modules
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { sendBagRequest } from '../../util/api';
import * as validators from '../../util/validators';

// Import ducks
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Import shared components
import {
  FieldSelect,
  FieldTextInput,
  Form,
  H1,
  H2,
  LayoutSingleColumn,
  Page,
  PrimaryButton,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './RequestBagPage.module.css';

const conditionOptions = [
  'new',
  'excellent',
  'veryGood',
  'good',
  'vintage',
  'any',
];

const normalizeValues = values => ({
  brand: values.brand || '',
  model: values.model || '',
  budget: values.budget || '',
  conditionPreference: values.conditionPreference || '',
  colorPreference: values.colorPreference || '',
  sizePreference: values.sizePreference || '',
  details: values.details || '',
  buyerName: values.buyerName || '',
  buyerEmail: values.buyerEmail || '',
  phone: values.phone || '',
});

/**
 * Buyer-facing form for requesting a specific handbag from the Bellemalle team.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @returns {JSX.Element}
 */
export const RequestBagPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;
  const [submitError, setSubmitError] = useState(null);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);

  const title = intl.formatMessage({ id: 'RequestBagPage.title' });

  const required = id => validators.required(intl.formatMessage({ id }));
  const emailRequired = required('RequestBagPage.buyerEmailRequired');
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'RequestBagPage.buyerEmailInvalid' })
  );

  const handleSubmit = values => {
    setSubmitError(null);
    setSubmitSucceeded(false);

    return sendBagRequest(normalizeValues(values))
      .then(() => {
        setSubmitSucceeded(true);
      })
      .catch(e => {
        setSubmitError(e);
      });
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={css.header}>
            <H1 rootClassName={css.title}>
              <FormattedMessage id="RequestBagPage.heading" />
            </H1>
            <p className={css.lead}>
              <FormattedMessage id="RequestBagPage.lead" />
            </p>
          </section>

          {submitSucceeded ? (
            <section className={css.successPanel} role="status">
              <H2 rootClassName={css.successTitle}>
                <FormattedMessage id="RequestBagPage.successTitle" />
              </H2>
              <p className={css.successMessage}>
                <FormattedMessage id="RequestBagPage.successMessage" />
              </p>
            </section>
          ) : (
            <FinalForm
              onSubmit={handleSubmit}
              initialValues={{ conditionPreference: '' }}
              render={formRenderProps => {
                const { handleSubmit, invalid, submitting } = formRenderProps;
                const submitDisabled = invalid || submitting;

                return (
                  <Form className={css.form} onSubmit={handleSubmit}>
                    <div className={css.section}>
                      <H2 rootClassName={css.sectionTitle}>
                        <FormattedMessage id="RequestBagPage.bagDetailsHeading" />
                      </H2>
                      <div className={css.twoColumnGrid}>
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.brand"
                          name="brand"
                          label={intl.formatMessage({ id: 'RequestBagPage.brandLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.brandPlaceholder',
                          })}
                          validate={required('RequestBagPage.brandRequired')}
                        />
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.model"
                          name="model"
                          label={intl.formatMessage({ id: 'RequestBagPage.modelLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.modelPlaceholder',
                          })}
                          validate={required('RequestBagPage.modelRequired')}
                        />
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.budget"
                          name="budget"
                          label={intl.formatMessage({ id: 'RequestBagPage.budgetLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.budgetPlaceholder',
                          })}
                          validate={required('RequestBagPage.budgetRequired')}
                        />
                        <FieldSelect
                          className={css.field}
                          id="requestBag.conditionPreference"
                          name="conditionPreference"
                          label={intl.formatMessage({
                            id: 'RequestBagPage.conditionPreferenceLabel',
                          })}
                          validate={required('RequestBagPage.conditionPreferenceRequired')}
                        >
                          <option value="">
                            {intl.formatMessage({
                              id: 'RequestBagPage.conditionPreferencePlaceholder',
                            })}
                          </option>
                          {conditionOptions.map(option => (
                            <option key={option} value={option}>
                              {intl.formatMessage({
                                id: `RequestBagPage.conditionPreference.${option}`,
                              })}
                            </option>
                          ))}
                        </FieldSelect>
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.colorPreference"
                          name="colorPreference"
                          label={intl.formatMessage({
                            id: 'RequestBagPage.colorPreferenceLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.colorPreferencePlaceholder',
                          })}
                        />
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.sizePreference"
                          name="sizePreference"
                          label={intl.formatMessage({
                            id: 'RequestBagPage.sizePreferenceLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.sizePreferencePlaceholder',
                          })}
                        />
                      </div>
                      <FieldTextInput
                        className={css.messageField}
                        type="textarea"
                        id="requestBag.details"
                        name="details"
                        label={intl.formatMessage({ id: 'RequestBagPage.detailsLabel' })}
                        placeholder={intl.formatMessage({
                          id: 'RequestBagPage.detailsPlaceholder',
                        })}
                      />
                    </div>

                    <div className={css.section}>
                      <H2 rootClassName={css.sectionTitle}>
                        <FormattedMessage id="RequestBagPage.buyerDetailsHeading" />
                      </H2>
                      <div className={css.twoColumnGrid}>
                        <FieldTextInput
                          className={css.field}
                          type="text"
                          id="requestBag.buyerName"
                          name="buyerName"
                          label={intl.formatMessage({ id: 'RequestBagPage.buyerNameLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.buyerNamePlaceholder',
                          })}
                          validate={required('RequestBagPage.buyerNameRequired')}
                        />
                        <FieldTextInput
                          className={css.field}
                          type="email"
                          id="requestBag.buyerEmail"
                          name="buyerEmail"
                          label={intl.formatMessage({ id: 'RequestBagPage.buyerEmailLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.buyerEmailPlaceholder',
                          })}
                          validate={validators.composeValidators(emailRequired, emailValid)}
                        />
                        <FieldTextInput
                          className={css.field}
                          type="tel"
                          id="requestBag.phone"
                          name="phone"
                          label={intl.formatMessage({ id: 'RequestBagPage.phoneLabel' })}
                          placeholder={intl.formatMessage({
                            id: 'RequestBagPage.phonePlaceholder',
                          })}
                        />
                      </div>
                    </div>

                    {submitError ? (
                      <p className={css.error} role="alert">
                        <FormattedMessage id="RequestBagPage.submitError" />
                      </p>
                    ) : null}

                    <div className={css.submitWrapper}>
                      <PrimaryButton
                        className={css.submitButton}
                        type="submit"
                        inProgress={submitting}
                        disabled={submitDisabled}
                      >
                        <FormattedMessage id="RequestBagPage.submitButton" />
                      </PrimaryButton>
                    </div>
                  </Form>
                );
              }}
            />
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

export default connect(mapStateToProps)(RequestBagPageComponent);
